import React, { useContext } from "react";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import {
  FreeTierPrice,
  PricingRateRows,
  type PricingLiveState,
  type ChargeState,
  type StorageChargeState,
} from "components/FreeTier";
import {
  isFreeTierInstance,
  getStoragePricing,
  roundDisplayPriceToCents,
  withServiceCharge,
  useFreeTierForUserId,
} from "utils/freeTierUtils";
import { isBrevCloud } from "utils/instanceTypes";
import { OrgContext } from "contexts/OrgContext";
import {
  RICH_TOOLTIP_CONTENT_CLASS_NAME,
  TOOLTIP_ARROW_CLASS_NAME,
} from "components/Environment/shared/DisabledActionTooltip";
import { getStorageCostNumber } from "../utils/index";
import { getEnvironmentBillingState } from "../utils/environtmentUtils";
import type { Environment } from "../utils/types";
import type { GPUInstanceType } from "types/InstanceTypes";

interface EnvironmentCostPanelProps {
  environment: Environment;
  appearance?: "default" | "inline" | "compact";
  tooltipDisabled?: boolean;
}

export const getEnvironmentCostDetails = (
  environment: Environment,
  instanceInfo: GPUInstanceType,
  isEntitled: boolean,
) => {
  const billingState = getEnvironmentBillingState(environment);
  const computePrice = withServiceCharge(instanceInfo.base_price?.amount);
  const diskGiB = Number(
    getStorageCostNumber(environment.instance?.disk_size || "") || 0,
  );
  const {
    pricePerGbHr,
    totalCost: storageCost,
    withinFreeTier: isStorageWithinFreeTier,
  } = getStoragePricing(instanceInfo, diskGiB);
  const isComputeFree =
    isEntitled && isFreeTierInstance(instanceInfo.base_price?.amount);
  const isStorageFree = isEntitled && isStorageWithinFreeTier;
  const storageBundled = !pricePerGbHr;
  const compute: ChargeState = !billingState.compute
    ? "idle"
    : isComputeFree
      ? "free"
      : "billing";
  const storage: StorageChargeState = (() => {
    if (!billingState.storage) {
      return storageBundled ? "included-idle" : "idle";
    }
    if (storageBundled) return "included-billing";
    return isStorageFree ? "free" : "billing";
  })();
  const liveTotal =
    (compute === "billing" ? roundDisplayPriceToCents(computePrice) : 0) +
    (storage === "billing" ? roundDisplayPriceToCents(storageCost) : 0);
  const activeFreeTotal =
    (compute === "free" ? roundDisplayPriceToCents(computePrice) : 0) +
    (storage === "free" ? roundDisplayPriceToCents(storageCost) : 0);

  return {
    activeFreeTotal,
    billingState,
    computePrice,
    isComputeFree,
    isStorageFree,
    liveState: { compute, storage } satisfies PricingLiveState,
    liveTotal,
    pricePerGbHr,
    showFreeTierPrice: liveTotal === 0 && activeFreeTotal > 0,
    storageBundled,
    storageCost,
  };
};

const EnvironmentCostPanel: React.FC<EnvironmentCostPanelProps> = ({
  environment,
  appearance = "default",
  tooltipDisabled = false,
}) => {
  const orgContext = useContext(OrgContext);
  const compactChargeClassName = (isAnythingBilling: boolean) =>
    `inline-flex min-h-6 shrink-0 items-center whitespace-nowrap border-0 bg-transparent p-0 text-sm leading-[24px] ${
      isAnythingBilling
        ? "font-semibold text-secondary"
        : "font-medium text-zinc-500"
    }`;
  const fallbackCharge =
    appearance === "compact" ? (
      <span className={compactChargeClassName(false)}>No active charges</span>
    ) : null;
  // Entitlement follows the env's creator (whose org-billing covers it),
  // not the viewer.
  const { isEntitled } = useFreeTierForUserId(environment.labels?.userId);

  const instanceTypeId =
    environment.instance?.instance_type || environment.instance_type?.type;
  const usesExternalBilling =
    Boolean(orgContext.activeOrg?.programId) || isBrevCloud(instanceTypeId);

  // Program orgs (academic grant, innovation, etc.) aren't billed by Brev,
  // and Brev-managed instances use a separate billing model. The standard
  // panel is misleading for both, but compact callers still show the rate.
  if (usesExternalBilling && appearance !== "compact") {
    return null;
  }

  const instanceInfo = (environment.instance?.instance_type_info ||
    environment.instance_type) as GPUInstanceType | undefined;
  if (!instanceInfo) return fallbackCharge;

  const {
    activeFreeTotal,
    billingState,
    computePrice,
    isComputeFree,
    isStorageFree,
    liveState,
    liveTotal,
    pricePerGbHr,
    showFreeTierPrice,
    storageBundled,
    storageCost,
  } = getEnvironmentCostDetails(environment, instanceInfo, isEntitled);

  const isAnythingBilling = liveTotal > 0;
  const chargeLabel = showFreeTierPrice ? (
    <FreeTierPrice price={activeFreeTotal.toFixed(2)} />
  ) : appearance === "compact" && !isAnythingBilling ? (
    "No active charges"
  ) : (
    `$${liveTotal.toFixed(2)}/hr`
  );
  const inactiveComputeHint =
    "Compute is not charged while the instance is inactive.";

  const hint = (() => {
    if (billingState.compute) {
      if (!billingState.storage) {
        return "Compute is currently charging. Storage is not charging.";
      }
      if (storageBundled || isStorageFree) return null;
      return "Stop the environment to pay only for storage.";
    }
    if (billingState.storage) {
      return `${inactiveComputeHint} Storage is still charging — delete to stop all charges.`;
    }
    return inactiveComputeHint;
  })();
  const triggerClassName =
    appearance === "compact"
      ? `${compactChargeClassName(isAnythingBilling)} cursor-help transition hover:brightness-150`
      : appearance === "inline"
        ? "flex items-center gap-1.5 text-sm text-secondary transition hover:brightness-150 cursor-help"
        : "flex cursor-help items-center gap-1.5 rounded-md border border-zinc-800 px-2 py-1 text-xs text-secondary transition hover:brightness-150";
  const staticClassName =
    appearance === "compact"
      ? compactChargeClassName(isAnythingBilling)
      : appearance === "inline"
        ? "flex items-center gap-1.5 text-sm text-secondary"
        : "flex items-center gap-1.5 rounded-md border border-zinc-800 px-2 py-1 text-xs text-secondary";
  const chargeDisplay =
    appearance === "compact" ? (
      chargeLabel
    ) : (
      <>
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            isAnythingBilling ? "bg-highlight" : "bg-zinc-600"
          }`}
          aria-hidden="true"
        />
        {chargeLabel}
      </>
    );

  if (tooltipDisabled || usesExternalBilling) {
    return <span className={staticClassName}>{chargeDisplay}</span>;
  }

  return (
    <TooltipPrimitives.Provider>
      <TooltipPrimitives.Root delayDuration={0}>
        <TooltipPrimitives.Trigger asChild>
          <button
            type="button"
            className={triggerClassName}
            onClick={(e) => e.stopPropagation()}
          >
            {chargeDisplay}
          </button>
        </TooltipPrimitives.Trigger>
        <TooltipPrimitives.Portal>
          <TooltipPrimitives.Content
            side="right"
            sideOffset={8}
            className={RICH_TOOLTIP_CONTENT_CLASS_NAME}
          >
            <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Current Charges
            </p>
            <PricingRateRows
              computePrice={computePrice}
              storageCost={storageCost}
              pricePerGbHr={pricePerGbHr}
              isFreeTierCompute={isComputeFree}
              isFreeTierStorage={isStorageFree}
              liveState={liveState}
            />
            {hint && (
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                {hint}
              </p>
            )}
            <TooltipPrimitives.Arrow className={TOOLTIP_ARROW_CLASS_NAME} />
          </TooltipPrimitives.Content>
        </TooltipPrimitives.Portal>
      </TooltipPrimitives.Root>
    </TooltipPrimitives.Provider>
  );
};

export default EnvironmentCostPanel;
